import React, { ReactNode, useState, useEffect } from 'react'
import { Alert } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'

interface ListPageLayoutProps {
  // Header
  title: string
  description: string
  icon: ReactNode
  backAction?: {
    label: string
    onClick: () => void
  }
  
  // Actions
  onNew?: () => void
  newButtonLabel?: string
  newButtonDisabled?: boolean
  
  // Search
  searchQuery: string
  onSearchChange: (query: string) => void
  searchPlaceholder?: string
  clearSearchLabel?: string
  searchActions?: ReactNode  // Actions to show next to search (like filters button)
  
  // View mode
  viewMode?: 'card' | 'table'
  onViewModeChange?: (mode: 'card' | 'table') => void
  // Called when the mobile breakpoint forces card mode (should NOT persist the preference)
  onMobileViewOverride?: (mode: 'card') => void
  showViewToggle?: boolean
  viewCardLabel?: string
  viewTableLabel?: string
  
  // Customization menu
  customizationMenu?: ReactNode  // Menu de configurações (tabela ou cards)
  
  // Chips de filtro rápido fixos exibidos na barra sticky
  stickyChips?: ReactNode
  // Row shown below search while the header remains sticky
  stickyToolbar?: ReactNode

  // Content
  children: ReactNode
  
  // States
  loading?: boolean
  error?: string | null
  success?: string | null
  
  // Optional extra filters/actions in header
  headerActions?: ReactNode
  // Extra element to render beside the title (e.g. totalizer)
  titleRight?: ReactNode
}

export function ListPageLayout({
  title,
  description,
  icon,
  backAction,
  onNew,
  newButtonLabel,
  newButtonDisabled = false,
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  clearSearchLabel,
  searchActions,
  viewMode = 'card',
  onViewModeChange,
  onMobileViewOverride,
  showViewToggle = false,
  viewCardLabel,
  viewTableLabel,
  customizationMenu,
  stickyChips,
  stickyToolbar,
  children,
  loading = false,
  error = null,
  success = null,
  headerActions
  , titleRight
}: ListPageLayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
    const { t } = useTranslation()
  const [showSearchHint, setShowSearchHint] = useState(false)
  const hintRef = React.useRef<HTMLDivElement | null>(null)
  const hintBtnRef = React.useRef<HTMLButtonElement | null>(null)

  const headerPaddingClass = isMobile ? 'py-2' : 'py-6'
  const headerMarginClass = isMobile ? 'mb-2' : 'mb-6'
  const searchGapClass = isMobile ? 'gap-3' : 'gap-3'
  const resolveLabel = (value: string | undefined, fallbackKey: string) => {
    const source = value ?? fallbackKey
    return source.includes('.') && !source.includes(' ') ? t(source) : source
  }
  const effectiveNewButtonLabel = newButtonLabel ?? t('list.newButton')
  const effectiveSearchPlaceholder = resolveLabel(searchPlaceholder, 'list.searchPlaceholder')
  const effectiveClearSearchLabel = resolveLabel(clearSearchLabel, 'list.clearSearch')
  const effectiveViewCardLabel = resolveLabel(viewCardLabel, 'list.viewCard')
  const effectiveViewTableLabel = resolveLabel(viewTableLabel, 'list.viewTable')

  // Detect scroll for sticky header shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (!mobile) setShowSearchHint(false)
      if (mobile) {
        if (onMobileViewOverride) onMobileViewOverride('card')
        else if (onViewModeChange) onViewModeChange('card')
      }
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [onViewModeChange])

  // Close hint when clicking outside
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!showSearchHint) return
      const t = e.target as Node
      if (hintRef.current?.contains(t) || hintBtnRef.current?.contains(t)) return
      setShowSearchHint(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [showSearchHint])

  return (
    <div className="min-h-screen">
      {/* Sticky Header Section */}
      <div className={`sticky top-0 z-10 bg-white dark:bg-slate-900 transition-shadow duration-200 ${
        isScrolled ? 'shadow-md' : ''
      }`}>
        <div className={`max-w-screen-2xl mx-auto px-4 lg:px-6 ${headerPaddingClass}`}>
          {/* Page header */}
          <div className={`flex items-center justify-between ${headerMarginClass}`}>
            <div className="flex min-w-0 flex-1 items-center gap-4">
              {backAction ? (
                <button
                  type="button"
                  onClick={backAction.onClick}
                  aria-label={backAction.label}
                  title={backAction.label}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-emerald-700 transition-colors hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 dark:text-emerald-400 dark:hover:bg-emerald-950/30 dark:hover:text-emerald-300"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m15 19-7-7 7-7" />
                  </svg>
                </button>
              ) : (
                <div className="shrink-0 rounded-lg bg-emerald-100 p-2 dark:bg-emerald-900/30">
                  {icon}
                </div>
              )}
              <div className="flex items-center gap-3 min-w-0">
                <div className="min-w-0">
                  <h1 className={`${isMobile ? 'text-lg' : 'text-2xl'} font-semibold text-slate-900 dark:text-white truncate`}>
                    {title}
                  </h1>
                  {!isMobile && (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {description}
                    </p>
                  )}
                </div>
                {titleRight && <div className="shrink-0 ml-2">{titleRight}</div>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {headerActions}
              {!isMobile && onNew && (
                <button
                  onClick={onNew}
                  disabled={newButtonDisabled}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {effectiveNewButtonLabel}
                </button>
              )}
            </div>
          </div>

          {/* Floating action button for mobile to save vertical header space */}
          {isMobile && onNew && (
            <button
              onClick={onNew}
              aria-label={effectiveNewButtonLabel}
              className="fixed bottom-4 right-4 z-20 md:hidden flex items-center justify-center w-12 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg transition-colors"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </button>
          )}

          {/* Search and filters section */}
          <div className={`flex items-center ${searchGapClass} w-full min-w-0`}>
            {/* Search group */}
            <div className="flex-1 min-w-0 flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-2.5 relative">
              <svg className="h-5 w-5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={(() => {
                  const full = effectiveSearchPlaceholder
                  if (!isMobile) return full
                  const paren = full.match(/\(([^)]+)\)/)
                  if (paren && paren[1]) return paren[1].trim()
                  if (full.length <= 32) return full
                  return t('list.compactSearchPlaceholder')
                })()}
                className="flex-1 min-w-0 text-sm bg-transparent text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none"
              />
              {/* Mobile hint button: shows full searchPlaceholder in a small popover */}
              {isMobile && (
                <>
                    <button
                    ref={hintBtnRef}
                    onClick={() => setShowSearchHint(s => !s)}
                    className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors shrink-0"
                    aria-label={t('list.searchHint')}
                      title={effectiveSearchPlaceholder}
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8h.01M11 12h1v4h1" />
                      <circle cx="12" cy="12" r="10" strokeWidth={2} />
                    </svg>
                  </button>
                  {showSearchHint && (
                    <div ref={hintRef} className="absolute right-3 top-full mt-2 z-40 w-[min(92%,20rem)] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-2 text-xs text-slate-700 dark:text-slate-300 shadow-lg">
                      {effectiveSearchPlaceholder}
                    </div>
                  )}
                </>
              )}
              {searchQuery && (
                <button
                  onClick={() => onSearchChange('')}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors shrink-0"
                  title={effectiveClearSearchLabel}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              
              {/* Search actions (filters, etc) */}
              {searchActions && (
                <>
                  <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 shrink-0" />
                  {searchActions}
                </>
              )}

              {/* Customization menu — inside search bar on mobile to avoid overflow */}
              {isMobile && customizationMenu && (
                <>
                  <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 shrink-0" />
                  <div className="hidden shrink-0">{customizationMenu}</div>
                </>
              )}
            </div>
            
            {/* View toggle and customization — desktop only (mobile gear is inside the search bar above) */}
            {showViewToggle && onViewModeChange && !isMobile && (
              <div className="flex items-center gap-2">
                {/* Botão alternado: apenas no desktop */}
                {showViewToggle && onViewModeChange && (
                  <button
                    onClick={() => onViewModeChange(viewMode === 'card' ? 'table' : 'card')}
                    className="p-2.5 rounded-lg transition-colors bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700"
                    title={viewMode === 'card' ? effectiveViewTableLabel : effectiveViewCardLabel}
                  >
                    {viewMode === 'card' ? (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    )}
                  </button>
                )}

                {/* Botão de configurações — apenas desktop */}
                {customizationMenu && (
                  <div className="hidden flex items-center">
                    {customizationMenu}
                  </div>
                )}
              </div>
            )}
          </div>

          {stickyToolbar && (
            <div className="mt-2">
              {stickyToolbar}
            </div>
          )}
        </div>

        {/* Preset chips — sticky, scroll horizontal no mobile */}
        {stickyChips && (
          <div className="-mt-3 pb-4 px-4 lg:px-6 py-5">
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar" style={{ scrollbarWidth: 'none' }}>
              {stickyChips}
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-6 py-2">
        {/* Alerts */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" className="mb-6">
            {success}
          </Alert>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        )}

        {/* Content */}
        {!loading && children}
      </div>
    </div>
  )
}
